import { useEffect, useState } from 'react';

import { EVENT_NAME, type OpalEvent } from '@frontend/constants';

export function useMessagingMode() {
  // `undefined` until the chat body reports a tab. It's distinct from a known
  // 'ai-chat': the host can mount after the body's initial mode broadcast and miss
  // it, so callers that need a positive tab must check `mode`, not `!isMessagingActive`.
  const [mode, setMode] = useState<'ai-chat' | 'messages' | undefined>(undefined);

  useEffect(() => {
    const handleModeChange = (event: OpalEvent<typeof EVENT_NAME.OPAL_IM_MODE_CHANGED>) => {
      setMode(event.detail.mode);
    };
    // The full-state snapshot also carries the mode and is emitted on state changes,
    // so honour it too — it refreshes the mode even when a modeChanged event was
    // missed (e.g. the overlay restored directly onto a tab).
    const handleStateChange = (event: OpalEvent<typeof EVENT_NAME.OPAL_IM_STATE_CHANGED>) => {
      setMode(event.detail.mode);
    };

    document.addEventListener(EVENT_NAME.OPAL_IM_MODE_CHANGED, handleModeChange);
    document.addEventListener(EVENT_NAME.OPAL_IM_STATE_CHANGED, handleStateChange);

    return () => {
      document.removeEventListener(EVENT_NAME.OPAL_IM_MODE_CHANGED, handleModeChange);
      document.removeEventListener(EVENT_NAME.OPAL_IM_STATE_CHANGED, handleStateChange);
    };
  }, []);

  // `isMessagingActive` stays false while the tab is unknown, preserving prior
  // behaviour for consumers that only care whether the messages tab is showing.
  return { mode, isMessagingActive: mode === 'messages' };
}
